'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useCartOptional } from '../contexts/CartContext';
import { getStoredStoreId } from '@/lib/store-storage';

type ShowcaseSlotKey =
  | 'afterHero'
  | 'afterPopularBrands'
  | 'afterCommunity'
  | 'afterBenefits'
  | 'afterFAQ'
  | 'afterExploreSection'
  | 'afterLocations'
  | 'recentDeals'
  | 'eventDetail'
  | 'blogPost'
  | 'productSelection';

type ShowcaseLayout = '1x4' | '2x4';

type FilterGroup = {
  field: string;
  thresholds: number[];
};

type ShowcaseSlot = {
  slotKey: ShowcaseSlotKey;
  title: string | null;
  collectionId: string | null;
  collectionHandle?: string | null;
  layout: ShowcaseLayout;
  productLimit: number;
  sortOrder: number;
  showTitle: boolean;
  filterGroup?: FilterGroup;
  products: any[];
  productsByThreshold?: Record<string, any[]>;
};

type PageShowcasesContextValue = {
  slotsByKey: Record<string, ShowcaseSlot | undefined>;
  loading: boolean;
  error: string | null;
};

const PageShowcasesContext = createContext<PageShowcasesContextValue>({
  slotsByKey: {},
  loading: true,
  error: null,
});

type PageShowcasesProviderProps = {
  children: React.ReactNode;
  endpoint?: string;
  /** When set (e.g. location detail URL), product lists use this store instead of cart/storage only. */
  covaLocationId?: number | null;
};

// Client-only wrapper to ensure cart context is available
function ClientOnlyPageShowcasesProvider({
  children,
  endpoint = '/api/homepage/product-showcases',
  covaLocationId: covaLocationIdProp = null,
}: PageShowcasesProviderProps) {
  const cart = useCartOptional();
  const state = cart?.state;
  const isInitialized = cart?.isInitialized ?? true;
  const storeIdFromCart = state?.storeCovaLocationId;
  const storeIdFromStorage = useMemo(() => getStoredStoreId(), []);
  const resolvedStoreId =
    covaLocationIdProp != null &&
    Number.isFinite(Number(covaLocationIdProp)) &&
    Number(covaLocationIdProp) > 0
      ? Number(covaLocationIdProp)
      : storeIdFromCart || storeIdFromStorage;

  const [slotsByKey, setSlotsByKey] = useState<Record<string, ShowcaseSlot | undefined>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const base = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:4000';

  useEffect(() => {
    if (!isInitialized) return;

    const fetchShowcases = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (resolvedStoreId) params.set('covaLocationId', String(resolvedStoreId));
        const url = `${base}${endpoint}${params.toString() ? `?${params.toString()}` : ''}`;

        const res = await fetch(url, { cache: 'no-store', credentials: 'include' });
        if (!res.ok) throw new Error(`Failed to load showcases: ${res.status}`);
        const data = await res.json();
        const slots: ShowcaseSlot[] = Array.isArray(data?.slots) ? data.slots : [];

        const map: Record<string, ShowcaseSlot | undefined> = {};
        slots.forEach((s) => {
          map[s.slotKey] = s;
        });
        setSlotsByKey(map);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        setSlotsByKey({});
      } finally {
        setLoading(false);
      }
    };

    fetchShowcases();
  }, [base, endpoint, isInitialized, resolvedStoreId]);

  return (
    <PageShowcasesContext.Provider value={{ slotsByKey, loading, error }}>{children}</PageShowcasesContext.Provider>
  );
}

export function usePageShowcases() {
  return useContext(PageShowcasesContext);
}

export function PageShowcasesProvider({
  children,
  endpoint = '/api/homepage/product-showcases',
  covaLocationId = null,
}: PageShowcasesProviderProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Only render the provider that uses cart context after mounting (client-side)
  if (!mounted) {
    return (
      <PageShowcasesContext.Provider value={{ slotsByKey: {}, loading: true, error: null }}>
        {children}
      </PageShowcasesContext.Provider>
    );
  }

  return (
    <ClientOnlyPageShowcasesProvider endpoint={endpoint} covaLocationId={covaLocationId}>
      {children}
    </ClientOnlyPageShowcasesProvider>
  );
}

export const HomepageShowcasesProvider = PageShowcasesProvider;
export const useHomepageShowcases = usePageShowcases;


